import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Box, Button, CircularProgress, Typography } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';

import { ConfirmOrder } from '../../redux/actions/billingActions';
import { CLEAR_NOTE_LIMIT } from '../../redux/actionTypes';

/**
 * LemonSqueezy redirects here after checkout with:
 *   ?order_id=<ls_order_id>&internal_order_id=<our_uuid>
 *
 * We call the backend to confirm the payment and activate the subscription.
 */
const BillingSuccessPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    // LS appends ?order_id=<N> to the redirect URL.
    // We embed internal_order_id in the redirect URL on the backend side,
    // and also keep sessionStorage as a fallback.
    const lsOrderId = searchParams.get('order_id');
    const internalOrderId =
      searchParams.get('internal_order_id') ||
      sessionStorage.getItem('ls_internal_order_id');

    // Debug log — visible in browser console if anything is missing
    console.debug('[BillingSuccess] params:', { lsOrderId, internalOrderId, allParams: Object.fromEntries(searchParams) });

    if (!internalOrderId) {
      setStatus('error');
      setErrorMsg('Missing order details. If you were charged, please contact support.');
      return;
    }

    // Clean up sessionStorage
    sessionStorage.removeItem('ls_internal_order_id');

    dispatch(ConfirmOrder(internalOrderId, lsOrderId))
      .then(() => {
        dispatch({ type: CLEAR_NOTE_LIMIT });
        setStatus('success');
        // Auto-redirect after 3 seconds
        setTimeout(() => navigate('/app'), 3000);
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || 'Payment verification failed. Contact support if you were charged.';
        setErrorMsg(msg);
        setStatus('error');
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="80vh"
      gap={3}
      px={3}
      textAlign="center"
    >
      {status === 'loading' && (
        <>
          <CircularProgress sx={{ color: '#7c3aed' }} size={48} />
          <Typography variant="h6" fontWeight={600}>
            Confirming your payment…
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait, this only takes a moment.
          </Typography>
        </>
      )}

      {status === 'success' && (
        <>
          <CheckCircleRoundedIcon sx={{ fontSize: 64, color: '#16a34a' }} />
          <Typography variant="h5" fontWeight={800}>
            You're all set!
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Your subscription is now active. Redirecting you to your notes…
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/app')}
            sx={{ borderRadius: '12px', px: 4, py: 1.25, fontWeight: 700, bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}
          >
            Go to Notes
          </Button>
        </>
      )}

      {status === 'error' && (
        <>
          <ErrorRoundedIcon sx={{ fontSize: 64, color: '#dc2626' }} />
          <Typography variant="h5" fontWeight={800}>
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary" maxWidth={360}>
            {errorMsg}
          </Typography>
          <Box display="flex" gap={2} mt={1}>
            <Button
              variant="outlined"
              onClick={() => navigate('/pricing')}
              sx={{ borderRadius: '12px', fontWeight: 600 }}
            >
              Back to Pricing
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/app')}
              sx={{ borderRadius: '12px', fontWeight: 600, bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}
            >
              Go to Notes
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default BillingSuccessPage;
